import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterStrip from "../components/NewsletterStrip";
import { ArticleCard, DealCard, DestinationCard, ReviewCard, prettyCategory } from "../components/ContentCards";
import { CardMini } from "../components/AffiliateBlocks";
import VideoBlogSection from "../components/VideoBlogSection";
import BestDealsHero from "../components/BestDealsHero";
import { ArrowUpRight } from "@phosphor-icons/react";

export default function HomePage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/homepage").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container-page py-24 text-center font-mono text-sm text-[#4A4742]">Loading…</div>
      </div>
    );
  }

  const { hero, latest_deals, travel_hacks, points_miles, airline_hotel_news, destinations, featured_review, featured_video, best_cards, latest_articles } = data;

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <Header />

      {/* Hero */}
      {hero && (
        <section className="container-page pt-10 md:pt-16 pb-16" data-testid="hero-section">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-5 order-2 md:order-1">
              <div className="overline text-[#B4533C] mb-4" data-testid="hero-category">{prettyCategory(hero.category)} · Editor's Pick</div>
              <h1 className="font-serif text-5xl md:text-6xl tracking-tight leading-[1.02] text-[#1C1A17]" data-testid="hero-title">
                {hero.title}
              </h1>
              <p className="mt-6 text-base text-[#4A4742] leading-relaxed max-w-md">{hero.excerpt}</p>
              <div className="mt-6 flex items-center gap-4 text-xs font-mono text-[#4A4742]">
                <span>By {hero.author}</span>
                {hero.reviewer && <><span>·</span><span>Reviewed by {hero.reviewer}</span></>}
                <span>·</span><span>{new Date(hero.updated_at).toLocaleDateString()}</span>
              </div>
              <Link to={`/article/${hero.slug}`} className="inline-flex items-center gap-2 mt-8 border border-[#1B3B36] text-[#1B3B36] px-6 py-3 text-sm tracking-wide hover:bg-[#1B3B36] hover:text-white transition-colors" data-testid="hero-read-cta">
                Read the story <ArrowUpRight size={16} weight="regular" />
              </Link>
            </div>
            <div className="md:col-span-7 order-1 md:order-2">
              <Link to={`/article/${hero.slug}`} className="block img-zoom overflow-hidden" data-testid="hero-image-link">
                <div className="aspect-[4/3] md:aspect-[5/4] bg-[#EFECE5]">
                  {hero.featured_image && <img src={hero.featured_image} alt={hero.title} className="w-full h-full object-cover" />}
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Best Deals This Week — auto-rotating hero band */}
      <BestDealsHero />

      {/* Latest Deals Row */}
      {latest_deals?.length > 0 && (
        <section className="container-page py-12 border-t border-[#D6CFC4]" data-testid="section-deals">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline text-[#B4533C]">Deal Alerts</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">Where the fares are moving</h2>
            </div>
            <Link to="/deals" className="overline text-[#1B3B36] hover:text-[#B4533C]">All deals →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {latest_deals.map((d) => <DealCard key={d.id} deal={d} />)}
          </div>
        </section>
      )}

      {/* Travel hacks grid */}
      {travel_hacks?.length > 0 && (
        <section className="container-page py-16 border-t border-[#D6CFC4]" data-testid="section-hacks">
          <div className="mb-8">
            <div className="overline text-[#B4533C]">Travel Hacks</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">Trending reader-first playbooks</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {travel_hacks.slice(0, 6).map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </section>
      )}

      {/* Points & Miles spotlight + best cards bento */}
      {(points_miles?.length > 0 || best_cards?.length > 0) && (
        <section className="container-page py-16 border-t border-[#D6CFC4]" data-testid="section-points">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="overline text-[#B4533C]">Points & Miles Spotlight</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 mb-8">Make your balances work harder</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {points_miles.slice(0, 4).map((a) => <ArticleCard key={a.id} article={a} />)}
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="overline text-[#B4533C]">Best Cards Right Now</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 mb-6">Editors' top picks</h2>
              <div className="space-y-5">
                {best_cards?.slice(0, 2).map((c) => <CardMini key={c.id} card={c} />)}
              </div>
              <Link to="/credit-cards" className="mt-6 block overline text-[#1B3B36] hover:text-[#B4533C]">Compare all cards →</Link>
            </div>
          </div>
        </section>
      )}

      {/* Airline and hotel news feed */}
      {airline_hotel_news?.length > 0 && (
        <section className="container-page py-16 border-t border-[#D6CFC4]" data-testid="section-news">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline text-[#B4533C]">Airlines & Hotels</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">News desk</h2>
            </div>
            <Link to="/airlines" className="overline text-[#1B3B36] hover:text-[#B4533C]">See all →</Link>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {airline_hotel_news.map((n) => (
              <Link to={`/article/${n.slug}`} key={n.id} className="group border-t border-[#D6CFC4] pt-5 flex gap-5" data-testid={`news-${n.slug}`}>
                <div className="img-zoom w-32 h-24 flex-shrink-0 overflow-hidden bg-[#EFECE5]">
                  {n.featured_image && <img src={n.featured_image} alt={n.title} className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline text-[#B4533C]">{prettyCategory(n.category)}</div>
                  <div className="font-serif text-lg leading-tight mt-1 group-hover:text-[#B4533C] transition-colors">{n.title}</div>
                  <div className="mt-2 text-xs font-mono text-[#4A4742]">{new Date(n.updated_at).toLocaleDateString()}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Destinations carousel */}
      {destinations?.length > 0 && (
        <section className="container-page py-16 border-t border-[#D6CFC4]" data-testid="section-destinations">
          <div className="mb-8">
            <div className="overline text-[#B4533C]">Destination Guides</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">Where to go next</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="md:col-span-2 md:row-span-2">
              {destinations[0] && <DestinationCard dest={destinations[0]} size="large" />}
            </div>
            {destinations.slice(1, 5).map((d) => <DestinationCard key={d.id} dest={d} />)}
          </div>
        </section>
      )}

      {/* Featured Review & Video Blog */}
      <section className="container-page py-16 border-t border-[#D6CFC4]" data-testid="section-review-video">
        <div className="grid md:grid-cols-2 gap-10">
          {featured_review && (
            <div>
              <div className="overline text-[#B4533C]">Featured Review</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 mb-6">The verdict is in</h2>
              <ReviewCard review={featured_review} />
            </div>
          )}
          {featured_video && (
            <div>
              <div className="overline text-[#B4533C]">Short of the week</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 mb-6">{featured_video.title}</h2>
              <div className="editorial-card">
                <div className="aspect-video bg-[#EFECE5] overflow-hidden">
                  {featured_video.thumbnail && <img src={featured_video.thumbnail} alt={featured_video.title} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <div className="overline text-[#4A4742]">Script Preview</div>
                  <p className="mt-2 text-sm leading-relaxed whitespace-pre-line line-clamp-4">{featured_video.script_text}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Video blog module (long-form episodes) */}
      <VideoBlogSection variant="home" />

      <NewsletterStrip />

      {/* Latest stories feed */}
      {latest_articles?.length > 0 && (
        <section className="container-page py-16" data-testid="section-latest">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline text-[#B4533C]">Latest from JetsetLess</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">Freshly published</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {latest_articles.slice(0, 9).map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
